import * as THREE from 'three';

// Runtime pose ownership only. Membership still comes exclusively from the
// canonical motionFollowerIdsByOwner contract consumed by AnimatedScenePart.
const closedBases = new WeakMap<THREE.Object3D, { world: THREE.Matrix4; parentWorld: THREE.Matrix4 }>();
const poses = new WeakMap<THREE.Object3D, PartMotionTransform>();
const motionCarriers = new WeakSet<THREE.Object3D>();
const dependents = new WeakMap<THREE.Object3D, Set<PartMotionTransform>>();

/** Exact closed frame, omitting only disposable motion matrices, never source
 * affine matrices. Walking ancestors also handles originally nested followers. */
export function closedSceneWorldMatrix(object: THREE.Object3D, target = new THREE.Matrix4()): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [];
  for (let node: THREE.Object3D | null = object; node; node = node.parent) chain.push(node);
  target.identity();
  for (const node of chain.reverse()) {
    if (motionCarriers.has(node)) continue;
    if (node.matrixAutoUpdate) node.updateMatrix();
    target.multiply(node.matrix);
  }
  return target;
}

/** RuntimeBuilder captures the canonical closed pose before applying persisted
 * module edits. Reopening an animation must keep its pivot in that same frame. */
export function registerPartMotionBase(object: THREE.Object3D): void {
  const parent = partMotionParent(object);
  closedBases.set(object, { world: closedSceneWorldMatrix(object),
    parentWorld: parent ? closedSceneWorldMatrix(parent) : new THREE.Matrix4() });
}

export function partMotionBaseFrame(object: THREE.Object3D): { world: THREE.Matrix4; parentWorld: THREE.Matrix4 } {
  const base = closedBases.get(object);
  const parent = partMotionParent(object);
  return base ? { world: base.world.clone(), parentWorld: base.parentWorld.clone() }
    : { world: closedSceneWorldMatrix(object), parentWorld: parent ? closedSceneWorldMatrix(parent) : new THREE.Matrix4() };
}

/** Same current closed owner delta used by the renderer and direct gesture. */
export function partMotionModuleDelta(object: THREE.Object3D): THREE.Matrix4 {
  return poses.get(object)?.moduleDelta()
    ?? closedSceneWorldMatrix(object).multiply(partMotionBaseFrame(object).world.invert());
}

export function partMotionParent(object: THREE.Object3D): THREE.Object3D | null {
  return poses.get(object)?.parentOf(object) ?? object.parent;
}

/** Module transforms update independent closed carriers first, then reconcile
 * each affected motion owner once, including followers in another section. */
export function syncPartMotions(objects: Iterable<THREE.Object3D>): void {
  const affected = new Set<PartMotionTransform>();
  for (const object of objects) for (const owner of dependents.get(object) ?? []) affected.add(owner);
  for (const owner of affected) owner.sync();
}

export class PartMotionTransform {
  private readonly members: { object: THREE.Object3D; parent: THREE.Object3D; carrier: THREE.Group }[] = [];
  private readonly watched = new Set<THREE.Object3D>();
  private readonly parents = new Map<THREE.Object3D, THREE.Object3D>();
  private readonly initialOwnerInverse: THREE.Matrix4;
  private readonly motion = new THREE.Matrix4();
  private disposed = false;

  constructor(private readonly owner: THREE.Object3D, followers: readonly THREE.Object3D[]) {
    const parts = [...new Set([owner, ...followers])].filter(part => part.parent);
    if (parts.some(part => poses.has(part))) throw new Error('PART_MOTION_OWNER_CONFLICT');
    const initialOwner = partMotionBaseFrame(owner).world;
    const invertible = (matrix: THREE.Matrix4) => matrix.elements.every(Number.isFinite)
      && Number.isFinite(matrix.determinant()) && matrix.determinant() !== 0;
    if (!invertible(initialOwner) || parts.some(part => !invertible(closedSceneWorldMatrix(part.parent!)))) {
      throw new Error('PART_MOTION_FRAME_SINGULAR');
    }
    this.initialOwnerInverse = initialOwner.invert();
    for (const object of parts) {
      const parent = object.parent!;
      const carrier = new THREE.Group();
      carrier.name = `part-motion:${object.name || object.uuid}`;
      carrier.matrixAutoUpdate = false;
      motionCarriers.add(carrier);
      parent.add(carrier);
      carrier.add(object); // Identity Q: retain the original source local matrix/TRS.
      this.members.push({ object, parent, carrier });
      poses.set(object, this);
      this.parents.set(object, parent);
      for (let node: THREE.Object3D | null = object; node; node = node.parent) {
        if (!motionCarriers.has(node)) this.watched.add(node);
      }
    }
    for (const object of this.watched) {
      const owners = dependents.get(object) ?? new Set<PartMotionTransform>();
      owners.add(this); dependents.set(object, owners);
    }
  }

  parentOf(object: THREE.Object3D): THREE.Object3D | null {
    return this.parents.get(object) ?? null;
  }

  setMotion(worldDelta: THREE.Matrix4): void {
    this.motion.copy(worldDelta);
    this.sync();
  }

  moduleDelta(): THREE.Matrix4 {
    return closedSceneWorldMatrix(this.owner).multiply(this.initialOwnerInverse);
  }

  sync(): void {
    if (this.disposed) return;
    const moduleDelta = this.moduleDelta();
    const worldMotion = moduleDelta.clone().multiply(this.motion).multiply(moduleDelta.clone().invert());
    // Parents first: a nested follower cancels inherited motion rather than
    // receiving the same delta a second time.
    const depth = (object: THREE.Object3D) => {
      let value = 0; for (let node = object.parent; node; node = node.parent) value++; return value;
    };
    for (const { parent, carrier } of [...this.members].sort((a,b) => depth(a.carrier)-depth(b.carrier))) {
      parent.updateWorldMatrix(true, false);
      carrier.matrix.copy(parent.matrixWorld).invert().multiply(worldMotion).multiply(closedSceneWorldMatrix(parent));
      carrier.matrixWorldNeedsUpdate = true;
      carrier.updateWorldMatrix(false, true);
    }
  }

  cleanup(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const { object, parent, carrier } of this.members) {
      parent.add(object); // No decomposition; current closed module pose survives.
      carrier.removeFromParent();
      poses.delete(object); motionCarriers.delete(carrier);
    }
    for (const object of this.watched) {
      const owners = dependents.get(object);
      owners?.delete(this); if (!owners?.size) dependents.delete(object);
    }
    for (const { object } of this.members) object.updateWorldMatrix(true, true);
  }
}
